import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PhoneIcon, BuildingStorefrontIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useAuth } from '../../contexts/AuthContext';

const ContactInformation = () => {
  const { user } = useAuth();
  const organizationName = user?.organization?.name || '';
  
  const [formData, setFormData] = useState({
    storeName: '',
    streetAddress: '',
    city: '',
    zipCode: '',
    emailAddress: '',
    phoneNumber: ''
  });
  
  const [originalData, setOriginalData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Use the unsaved changes hook
  useUnsavedChanges(hasUnsavedChanges, isEditing);

  // Load contact information from API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('/api/settings/organization/contact');
        const { data } = response.data;
        
        // Use organization name as default if storeName is not set
        const contactData = {
          storeName: data.storeName || organizationName || '',
          streetAddress: data.streetAddress || '',
          city: data.city || '',
          zipCode: data.zipCode || '',
          emailAddress: data.emailAddress || '',
          phoneNumber: data.phoneNumber || ''
        };
        
        setFormData(contactData);
        setOriginalData(contactData);
      } catch (error) {
        console.error('Error fetching contact information:', error);
        // Initialize with organization name if fetch fails
        const initialData = {
          storeName: organizationName || '',
          streetAddress: '',
          city: '',
          zipCode: '',
          emailAddress: '',
          phoneNumber: ''
        };
        setFormData(initialData);
        setOriginalData(initialData);
      }
    };
    
    fetchContactInfo();
  }, [organizationName]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalData]);

  // Unsaved changes protection is handled by the useUnsavedChanges hook

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Send data in the format expected by the new modular endpoint
      const contactData = {
        storeName: formData.storeName,
        streetAddress: formData.streetAddress,
        city: formData.city,
        zipCode: formData.zipCode,
        emailAddress: formData.emailAddress,
        phoneNumber: formData.phoneNumber
      };
      
      console.log('Saving contact information:', contactData);
      
      await axios.put('/api/settings/organization/contact', contactData);
      
      setOriginalData({ ...formData });
      setHasUnsavedChanges(false);
      setIsEditing(false);
      alert('Contact information saved successfully!');
    } catch (error) {
      console.error('Error saving contact information:', error);
      alert('Failed to save contact information. Please try again.');
    }
  };

  const handleDiscard = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Your changes have not been saved - are you sure you want to leave this page?')) {
        setFormData({ ...originalData });
        setHasUnsavedChanges(false);
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  // Check if we have any saved data to display
  const hasData = originalData.storeName || originalData.streetAddress || originalData.city || 
                  originalData.zipCode || originalData.emailAddress || originalData.phoneNumber;

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Contact Information
          </h2>
          <p className="mt-1 text-gray-600">
            Manage your store's contact details and customer communication preferences
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <BuildingStorefrontIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
            </div>
            {!isEditing && hasData && (
              <button
                onClick={handleEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-colors duration-200"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>
            )}
          </div>

          {!isEditing && hasData ? (
            // View Mode - Display saved information
            <div className="space-y-6">
              {/* Store Information Display */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Store Information</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {originalData.storeName && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Store Name:</span>
                        <p className="text-gray-900 mt-1">{originalData.storeName}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Address Display */}
              {(originalData.streetAddress || originalData.city || originalData.zipCode) && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Address</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {originalData.streetAddress && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Street Address:</span>
                          <p className="text-gray-900 mt-1">{originalData.streetAddress}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {originalData.city && (
                          <div>
                            <span className="text-sm font-medium text-gray-500">City:</span>
                            <p className="text-gray-900 mt-1">{originalData.city}</p>
                          </div>
                        )}
                        {originalData.zipCode && (
                          <div>
                            <span className="text-sm font-medium text-gray-500">Zip Code:</span>
                            <p className="text-gray-900 mt-1">{originalData.zipCode}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Details Display */}
              {(originalData.emailAddress || originalData.phoneNumber) && (
                <div>
                  <div className="flex items-center mb-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <h4 className="text-md font-medium text-gray-900">Contact Details</h4>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {originalData.emailAddress && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Email Address:</span>
                          <p className="text-gray-900 mt-1">{originalData.emailAddress}</p>
                        </div>
                      )}
                      {originalData.phoneNumber && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Phone Number:</span>
                          <p className="text-gray-900 mt-1">{originalData.phoneNumber}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Edit Mode or No Data - Show form
            <form className="space-y-6">
              {!hasData && !isEditing && (
                <div className="text-center py-8 text-gray-500">
                  <BuildingStorefrontIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="mb-4">No contact information set up yet.</p>
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Add Contact Information
                  </button>
                </div>
              )}

              {isEditing && (
                <>
                  {/* Store Name */}
                  <div>
                    <div className="flex items-center mb-4">
                      <BuildingStorefrontIcon className="h-6 w-6 text-gray-400 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">Store Information</h3>
                    </div>
                    <div>
                      <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-2">
                        Store Name
                      </label>
                      <input
                        type="text"
                        id="storeName"
                        value={formData.storeName}
                        onChange={(e) => handleInputChange('storeName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your store name"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Address</h4>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-2">
                          Street Address
                        </label>
                        <input
                          type="text"
                          id="streetAddress"
                          value={formData.streetAddress}
                          onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter street address"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                            City
                          </label>
                          <input
                            type="text"
                            id="city"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter city"
                          />
                        </div>
                        <div>
                          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                            Zip Code
                          </label>
                          <input
                            type="text"
                            id="zipCode"
                            value={formData.zipCode}
                            onChange={(e) => handleInputChange('zipCode', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter zip code"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div>
                    <div className="flex items-center mb-4">
                      <PhoneIcon className="h-6 w-6 text-gray-400 mr-2" />
                      <h4 className="text-md font-medium text-gray-900">Contact Details</h4>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="emailAddress"
                          value={formData.emailAddress}
                          onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleDiscard}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={!hasUnsavedChanges}
                      className={`px-6 py-2 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 ${
                        hasUnsavedChanges
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactInformation;